import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  Loader2,
  Shield,
  BarChart3,
  Bell,
  CheckCircle,
  Building2,
  Briefcase,
  Leaf,
} from 'lucide-react';
import { useAuth, ROLES } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading: authLoading, error, clearError } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);


  const isLoading = isSubmitting;


  const validRoutes = ['/', '/loans', '/alerts', '/import', '/reports', '/settings', '/profile', '/help', '/team', '/company', '/notifications', '/roles'];
  const requestedPath = location.state?.from?.pathname;

  const isValidRoute = requestedPath && (validRoutes.includes(requestedPath) || requestedPath.startsWith('/loans/'));
  const from = isValidRoute ? requestedPath : '/';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    clearError();
    setLocalError('');
  };

  const validateForm = () => {
    if (!formData.email) {
      setLocalError(t('auth.enterEmail'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setLocalError(t('auth.invalidEmail'));
      return false;
    }
    if (!formData.password) {
      setLocalError(t('auth.enterPassword'));
      return false;
    }
    if (formData.password.length < 6) {
      setLocalError(t('auth.passwordTooShort'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await login(formData.email, formData.password, formData.rememberMe);

      if (result.success) {
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      setLocalError(err.message || 'An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: BarChart3,
      titleKey: 'auth.featureRealTime',
      descKey: 'auth.featureRealTimeDesc',
    },
    {
      icon: Bell,
      titleKey: 'auth.featureAlerts',
      descKey: 'auth.featureAlertsDesc',
    },
    {
      icon: Shield,
      titleKey: 'auth.featureSecurity',
      descKey: 'auth.featureSecurityDesc',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Covenant Tracker</span>
          </div>

          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-white mb-6">
              {t('auth.heroTitle')}
            </h1>
            <p className="text-blue-100 text-lg mb-12">
              {t('auth.heroSubtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{t(feature.titleKey)}</h3>
                  <p className="text-blue-200 text-sm">{t(feature.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-blue-200 text-sm">
          {t('auth.copyright')}
        </div>
      </div>

      {}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800">Covenant Tracker</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                {t('auth.login')}
              </h2>
              <p className="text-slate-500">
                {t('auth.welcomeBack')}
              </p>
            </div>

            {}
            {(error || localError) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error || localError}</p>
              </div>
            )}

            {}
            {location.state?.registered && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-green-700 text-sm">
                  {t('auth.registerSuccess')}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              {}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-11 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-600">{t('auth.rememberMe')}</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              {}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.loginButton')
                )}
              </button>
            </form>

            {}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">
                  {t('auth.noAccount')}
                </span>
              </div>
            </div>

            {}
            <Link
              to="/register"
              className="w-full py-3 px-4 border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-slate-700 font-semibold rounded-lg transition-colors flex items-center justify-center"
            >
              {t('auth.registerButton')}
            </Link>
          </div>

          {}
          <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
                <Shield className="w-3.5 h-3.5 text-white" />
              </div>
              <p className="text-blue-800 text-sm font-semibold">LMA Edge Hackathon - Demo Accounts</p>
            </div>

            {}
            <div className="mb-3">
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    email: 'agent@lma-demo.com',
                    password: 'demo2025',
                  }));
                  clearError();
                  setLocalError('');
                }}
                className="w-full p-3 bg-purple-100 hover:bg-purple-200 border border-purple-300 rounded-lg transition-colors text-left flex items-start gap-3"
              >
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-purple-800 block">Facility Agent</span>
                  <span className="text-xs text-purple-600 block">LMA Agency Services</span>
                  <span className="text-xs text-purple-500 block truncate">agent@lma-demo.com</span>
                </div>
                <span className="text-xs bg-purple-200 text-purple-700 px-2 py-1 rounded-full self-start">Full Access</span>
              </button>
            </div>

            {}
            <p className="text-xs text-blue-600 font-medium mb-2 uppercase tracking-wider">Lenders</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { company: 'BNP Paribas', email: 'lender@bnp-demo.com' },
                { company: 'HSBC', email: 'lender@hsbc-demo.com' },
              ].map((account) => (
                <button
                  key={account.email}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      email: account.email,
                      password: 'demo2025',
                    }));
                    clearError();
                    setLocalError('');
                  }}
                  className="p-2 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-lg transition-colors text-left flex items-center gap-2"
                >
                  <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3 h-3 text-white" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-blue-800 text-xs block truncate">{account.company}</span>
                    <span className="text-xs text-blue-500 block truncate">{account.email}</span>
                  </div>
                </button>
              ))}
            </div>

            {}
            <p className="text-xs text-emerald-600 font-medium mb-2 uppercase tracking-wider">Borrowers</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { company: 'ACME Industries', email: 'borrower@acme-demo.com', esg: false },
                { company: 'GreenTech SA', email: 'borrower@greentech-demo.com', esg: true },
              ].map((account) => (
                <button
                  key={account.email}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      email: account.email,
                      password: 'demo2025',
                    }));
                    clearError();
                    setLocalError('');
                  }}
                  className="p-2 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 rounded-lg transition-colors text-left flex items-center gap-2"
                >
                  <div className="w-6 h-6 bg-emerald-500 rounded-md flex items-center justify-center flex-shrink-0">
                    {account.esg ? <Leaf className="w-3 h-3 text-white" /> : <Briefcase className="w-3 h-3 text-white" />}
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-emerald-800 text-xs block truncate">{account.company}</span>
                    <span className="text-xs text-emerald-500 block truncate">{account.email}</span>
                  </div>
                </button>
              ))}
            </div>

            <p className="text-xs text-slate-500 mt-3 text-center">Password for all accounts: <code className="bg-slate-200 px-1 py-0.5 rounded text-slate-700">demo2025</code></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
